import { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Bot, Send, Sparkles, User as UserIcon, Loader2, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

const SUGGESTED_QUESTIONS = [
  'Quali contratti scadono nei prossimi 30 giorni?',
  'Quanto ha venduto Venditore Test negli ultimi 3 mesi?',
  'Qual è il ticket medio di questo anno?',
  'Come è variata la % di rinnovi nel tempo?',
  'Quali sono i KPI globali di oggi?',
  'Top 5 progetti per Success Fee',
];

// ─────────────────────────────────────────────────────────────
// Table rendering config per tool
// ─────────────────────────────────────────────────────────────
const fmtCur = (v) => (v == null || v === '') ? '-' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString('it-IT');
  } catch { return String(v).slice(0, 10); }
};

const TOOL_TABLE_CONFIGS = {
  list_users: {
    listKey: 'users',
    columns: [
      { label: 'Nome', field: 'name' },
      { label: 'Email', field: 'email' },
      { label: 'Ruoli', field: 'roles', render: (v) => (v || []).join(', ') },
    ],
  },
  get_top_projects: {
    listKey: 'top',
    columns: [
      { label: '#', render: (_row, i) => i + 1 },
      { label: 'Progetto', field: 'project_name' },
      { label: 'Importo', field: 'amount_formatted' },
    ],
  },
  get_contracts_expiring: {
    listKey: 'items',
    columns: [
      { label: 'Contratto', field: 'contract_name' },
      { label: 'Cliente', field: 'client_name' },
      { label: 'Valore', field: 'value', render: fmtCur },
      { label: 'Scadenza', field: 'end_date', render: fmtDate },
    ],
  },
  get_contracts_expired: {
    listKey: 'items',
    columns: [
      { label: 'Contratto', field: 'contract_name' },
      { label: 'Valore', field: 'value', render: fmtCur },
      { label: 'Status', field: 'status' },
      { label: 'Scaduto', field: 'end_date', render: fmtDate },
    ],
  },
  get_contracts_by_user: {
    listKey: 'contracts',
    columns: [
      { label: 'Nome', field: 'name' },
      { label: 'Valore', field: 'value', render: fmtCur },
      { label: 'Firma', field: 'signing_date', render: fmtDate },
    ],
  },
  get_sales_by_user: {
    listKey: 'sales_preview',
    columns: [
      { label: 'Data', field: 'date', render: fmtDate },
      { label: 'Importo', field: 'amount', render: fmtCur },
      { label: 'SF', field: 'commission', render: fmtCur },
    ],
  },
  get_success_fee_by_type: {
    listKey: 'by_type',
    columns: [
      { label: 'Tipologia', field: 'type' },
      { label: 'Totale', field: 'amount_formatted' },
    ],
  },
  get_renewal_rate: {
    listKey: 'series',
    columns: [
      { label: 'Periodo', field: 'period' },
      { label: 'Tot', field: 'total' },
      { label: 'Rinnovati', field: 'renewed' },
      { label: 'Persi', field: 'lost' },
      { label: 'Recessi', field: 'withdrawn' },
      { label: 'Rinn %', field: 'renewal_rate_pct', render: (v) => `${v}%` },
    ],
  },
  get_customer_feedback_summary: {
    listKey: 'recent',
    columns: [
      { label: 'Data', field: 'date', render: fmtDate },
      { label: 'NPS', field: 'nps' },
      { label: 'Commento', field: 'comment' },
    ],
  },
};

function ToolResultTable({ toolUsed, result }) {
  if (!toolUsed || !result || typeof result !== 'object') return null;
  if (result.__error) return null;
  const cfg = TOOL_TABLE_CONFIGS[toolUsed];
  if (!cfg) return null;
  const rows = result[cfg.listKey];
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return (
    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="chatbot-inline-table">
      <div className="max-h-72 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {cfg.columns.map((c) => (
                <TableHead key={c.label} className="text-[11px] uppercase tracking-wider py-1.5 px-2">{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 30).map((row, i) => (
              <TableRow key={i}>
                {cfg.columns.map((c) => {
                  const raw = c.field ? row[c.field] : null;
                  const val = c.render ? c.render(raw, i, row) : raw;
                  return (
                    <TableCell key={c.label} className="text-xs py-1.5 px-2 whitespace-nowrap">
                      {val == null || val === '' ? '-' : String(val).slice(0, 80)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length > 30 && (
        <div className="text-[11px] text-gray-400 px-2 py-1 bg-gray-50 border-t">
          Mostrati primi 30 di {rows.length} risultati.
        </div>
      )}
    </div>
  );
}

export default function ChatbotDrawer() {
  const { hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Carica ultima sessione al primo open del drawer
  useEffect(() => {
    if (!open || historyLoaded) return;
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const list = await api.get('/chatbot/sessions');
        const sessions = list.data?.sessions || [];
        if (sessions.length === 0 || cancelled) {
          setHistoryLoaded(true);
          setLoadingHistory(false);
          return;
        }
        const lastId = sessions[0].id;
        const detail = await api.get(`/chatbot/sessions/${lastId}`);
        if (cancelled) return;
        const raw = detail.data?.messages || [];
        const mapped = raw.map((m) => ({
          role: m.role,
          content: m.content,
          tool_used: m.meta?.tool_used,
          tool_result: m.meta?.tool_result,
          cost_usd: m.meta?.cost_usd,
          cached: m.meta?.cached,
          error: !!m.meta?.error,
        }));
        setMessages(mapped);
        setSessionId(lastId);
      } catch (err) {
        console.error('Load history failed', err);
      } finally {
        if (!cancelled) {
          setHistoryLoaded(true);
          setLoadingHistory(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, historyLoaded]);

  if (!hasRole(['admin'])) return null;

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setLoading(true);
    try {
      const res = await api.post('/chatbot/query', { question, session_id: sessionId });
      const d = res.data || {};
      if (d.session_id) setSessionId(d.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: d.answer || 'Nessuna risposta.',
          tool_used: d.tool_used,
          tool_result: d.tool_result,
          cost_usd: d.cost_usd,
          cached: d.cached,
          error: !!d.error,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Errore: ${err.response?.data?.detail || err.message}`, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setSessionId(null);
    setHistoryLoaded(true); // non ricarica history dopo un clear manuale
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          className="fixed bottom-6 right-6 z-50 rounded-full h-14 w-14 shadow-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 border-0"
          size="icon"
          data-testid="chatbot-open-btn"
          title="Apri assistente AI"
        >
          <Sparkles className="h-6 w-6 text-white" />
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="chatbot-drawer">
        <SheetHeader className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <span>Assistente VAD Brain</span>
            <span className="ml-auto text-xs font-normal text-gray-500">Claude Haiku</span>
          </SheetTitle>
          <p className="text-xs text-gray-500 mt-0.5">Chiedi qualsiasi cosa su vendite, contratti, success fee, rinnovi, feedback.</p>
        </SheetHeader>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chatbot-messages">
          {loadingHistory && (
            <div className="flex items-center justify-center py-4 text-sm text-gray-500 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carico cronologia…
            </div>
          )}

          {!loadingHistory && messages.length === 0 && !loading && (
            <div className="text-center py-6">
              <Bot className="h-10 w-10 text-indigo-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-4">Prova a chiedere...</p>
              <div className="flex flex-col gap-2">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="text-left text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-gray-700"
                    data-testid={`suggested-q-${SUGGESTED_QUESTIONS.indexOf(q)}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${m.role === 'user' ? 'bg-gray-200' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                {m.role === 'user' ? <UserIcon className="h-4 w-4 text-gray-700" /> : <Bot className="h-4 w-4 text-white" />}
              </div>
              <div className={`flex-1 max-w-[85%] ${m.role === 'user' ? 'text-right' : ''}`}>
                <div
                  className={`inline-block text-left px-3.5 py-2.5 rounded-2xl text-sm ${
                    m.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-tr-sm'
                      : m.error
                      ? 'bg-red-50 border border-red-200 text-red-800 rounded-tl-sm'
                      : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                  }`}
                  data-testid={`msg-${i}`}
                >
                  {m.error && <AlertCircle className="h-4 w-4 inline mr-1" />}
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                </div>
                {m.role === 'assistant' && !m.error && (
                  <ToolResultTable toolUsed={m.tool_used} result={m.tool_result} />
                )}
                {m.role === 'assistant' && !m.error && (
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-400">
                    {m.tool_used && <span className="px-1.5 py-0.5 bg-gray-100 rounded">🔧 {m.tool_used}</span>}
                    {m.cached && <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded">cached</span>}
                    {m.cost_usd != null && <span>${Number(m.cost_usd).toFixed(5)}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-2">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="px-3.5 py-2.5 bg-gray-100 rounded-2xl rounded-tl-sm">
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t p-3 bg-white">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={clear}
              className="text-xs text-gray-500 hover:text-gray-700 underline mb-2"
              data-testid="clear-chat-btn"
            >
              Nuova conversazione
            </button>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex gap-2 items-end"
          >
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi la tua domanda... (Invio per inviare)"
              className="resize-none min-h-[42px] max-h-32 text-sm"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              data-testid="chatbot-input"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              size="icon"
              className="h-[42px] w-[42px] flex-shrink-0"
              data-testid="chatbot-send-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
